import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { DamageReport } from '../types';

const damageReportsCollection = collection(db, 'damageReports');

/**
 * Create a new damage report
 */
export const createDamageReport = async (
  reportData: Omit<DamageReport, 'id' | 'createdAt' | 'updatedAt'>
): Promise<{ id: string }> => {
  const newReport = {
    ...reportData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(damageReportsCollection, newReport);
  return { id: docRef.id };
};

/**
 * Get a damage report by ID
 */
export const getDamageReport = async (reportId: string): Promise<DamageReport> => {
  const reportRef = doc(damageReportsCollection, reportId);
  const reportDoc = await getDoc(reportRef);

  if (!reportDoc.exists()) {
    throw new Error(`Damage report with ID ${reportId} not found`);
  }

  return { id: reportDoc.id, ...reportDoc.data() } as DamageReport;
};

/**
 * Get all damage reports for a project
 */
export const getDamageReportsByProject = async (projectId: string): Promise<DamageReport[]> => {
  const q = query(
    damageReportsCollection,
    where('projectId', '==', projectId),
    orderBy('reportedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DamageReport));
};

/**
 * Get all damage reports for a specific item
 */
export const getDamageReportsByItem = async (projectId: string, itemId: string): Promise<DamageReport[]> => {
  const q = query(
    damageReportsCollection,
    where('projectId', '==', projectId),
    where('itemId', '==', itemId),
    orderBy('reportedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DamageReport));
};

/**
 * Update a damage report
 */
export const updateDamageReport = async (
  reportId: string,
  data: Partial<Omit<DamageReport, 'id' | 'projectId' | 'itemId' | 'createdAt'>>
): Promise<void> => {
  const reportRef = doc(damageReportsCollection, reportId);
  
  await updateDoc(reportRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
};

/**
 * Delete a damage report
 */
export const deleteDamageReport = async (reportId: string): Promise<void> => {
  await deleteDoc(doc(damageReportsCollection, reportId));
};

/**
 * Add a photo to a damage report
 */
export const addPhotoToDamageReport = async (reportId: string, photoId: string): Promise<void> => {
  const reportRef = doc(damageReportsCollection, reportId);
  const reportDoc = await getDoc(reportRef);

  if (!reportDoc.exists()) {
    throw new Error(`Damage report with ID ${reportId} not found`);
  }

  const report = reportDoc.data() as DamageReport;
  const photoIds = [...(report.photoIds || []), photoId];

  await updateDoc(reportRef, {
    photoIds,
    updatedAt: serverTimestamp()
  });
};

/**
 * Remove a photo from a damage report
 */
export const removePhotoFromDamageReport = async (reportId: string, photoId: string): Promise<void> => {
  const reportRef = doc(damageReportsCollection, reportId);
  const reportDoc = await getDoc(reportRef);

  if (!reportDoc.exists()) {
    throw new Error(`Damage report with ID ${reportId} not found`);
  }

  const report = reportDoc.data() as DamageReport;
  const photoIds = (report.photoIds || []).filter(id => id !== photoId);

  await updateDoc(reportRef, {
    photoIds,
    updatedAt: serverTimestamp()
  });
}; 